import { computed, toValue, type MaybeRef, type Ref } from 'vue'

import { apiFetch } from '@ja/portal-query'
import { useRealtimeSubscription } from '@ja/realtime'
import type { RealtimeFrame } from '@ja/realtime'

export interface ConversationMessageEvent {
  action: 'message_created'
  conversationId: string
  messageId: string
  content: string
  senderId: string
  senderName: string
  hasAttachments: boolean
  timestamp: string
}

export interface ConversationReadEvent {
  action: 'message_read'
  conversationId: string
  messageId: string
  readerId: string
  readerName: string
  readAt: string
}

export interface ConversationTypingEvent {
  action: 'typing'
  conversationId: string
  userId: string
  userName: string
  isTyping: boolean
}

type ConversationPayload =
  | ConversationMessageEvent
  | ConversationReadEvent
  | ConversationTypingEvent

export interface UseConversationChannelOptions {
  onMessageCreated?: (event: ConversationMessageEvent) => void
  onMessageRead?: (event: ConversationReadEvent) => void
  onTyping?: (event: ConversationTypingEvent) => void
  currentUserId?: string
  skipOwnEvents?: boolean
}

export interface UseConversationChannelReturn {
  isConnected: Ref<boolean>
  sendTypingIndicator: (isTyping: boolean) => Promise<void>
}

/**
 * Subscribe to `conversation:<id>` — dynamic resource channel registered by
 * Core\Communication\Base\CommunicationServiceProvider under owner
 * `communication`, authorized by ConversationChannelAuthorizer (defers to
 * ConversationService::canUserViewConversation). Dispatches on wire events
 * `conversation.message`, `conversation.read`, `conversation.typing`.
 */
export function useConversationChannel(
  conversationId: MaybeRef<string | null | undefined>,
  options: UseConversationChannelOptions = {},
): UseConversationChannelReturn {
  const channel = computed(() => {
    const id = toValue(conversationId)
    return id ? `conversation:${id}` : ''
  })

  const shouldSkipEvent = (senderId: string): boolean => {
    if (!options.skipOwnEvents || !options.currentUserId) {
      return false
    }
    return senderId === options.currentUserId
  }

  const { isConnected } = useRealtimeSubscription<ConversationPayload>(channel, {
    onMessage(frame: RealtimeFrame<ConversationPayload>) {
      if (frame.event === 'conversation.message') {
        const event = frame.data as ConversationMessageEvent
        if (!shouldSkipEvent(event.senderId)) {
          options.onMessageCreated?.(event)
        }
      } else if (frame.event === 'conversation.read') {
        const event = frame.data as ConversationReadEvent
        if (!shouldSkipEvent(event.readerId)) {
          options.onMessageRead?.(event)
        }
      } else if (frame.event === 'conversation.typing') {
        const event = frame.data as ConversationTypingEvent
        if (!shouldSkipEvent(event.userId)) {
          options.onTyping?.(event)
        }
      }
    },
  })

  const sendTypingIndicator = async (isTyping: boolean): Promise<void> => {
    const id = toValue(conversationId)
    if (!id) return

    try {
      await apiFetch(`/api/conversations/${id}/typing`, {
        method: 'POST',
        body: JSON.stringify({ is_typing: isTyping }),
      })
    } catch {
      // typing indicators are non-critical
    }
  }

  return {
    isConnected,
    sendTypingIndicator,
  }
}
